import { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { keyCodeOf } from '@/lib/tagShortcuts';

// Ctrl held + key pressed three times quickly (e.g. Ctrl + B B B).
export function useTripleKey(code, enabled, onOpen, blockAll = false) {
  useEffect(() => {
    if (!enabled) return undefined;
    let hits = [];
    const onKey = (e) => {
      if (!e.ctrlKey || e.altKey || e.shiftKey || e.metaKey || keyCodeOf(e) !== code) { if (e.key !== 'Control') hits = []; return; }
      if (blockAll) e.preventDefault();
      const now = Date.now();
      hits = [...hits.filter((t) => now - t < 1500), now];
      if (hits.length >= 3) { e.preventDefault(); hits = []; onOpen(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [code, enabled, onOpen, blockAll]);
}

// Portal lock: sessions from before the lock are logged out, newer ones see the "expired" page.
// Sales & Approval mode (partial lock), read by dialogs that need it.
export const PORTAL_MODE = { partial: false };

export function usePortalLock({ onUnlock, onEnded }) {
  const [locked, setLocked] = useState(false);
  const [partial, setPartial] = useState(false);
  const [portalOpen, setPortalOpen] = useState(null); // null until the first check: fully unlocked for everyone?
  const partialRef = useRef(null);
  const [isSuper, setIsSuper] = useState(false);
  const lockedRef = useRef(false);
  const cb = useRef({ onUnlock, onEnded });
  cb.current = { onUnlock, onEnded };
  const check = useCallback(async () => {
    try {
      const { data } = await notesApi.get('/notes/tag-workspace/lock-status');
      if (data?.session_ended) { cb.current.onEnded(); return; }
      if (lockedRef.current && !data?.locked) cb.current.onUnlock();
      setIsSuper(!!data?.is_tag_super);
      setPortalOpen(!!data?.portal_open && !data?.locked);
      const p = !!data?.partial;
      // Entering or leaving Sales & Approval mode reloads so every list follows the new scope.
      if (partialRef.current !== null && partialRef.current !== p && !data?.locked) { window.location.reload(); return; }
      if (!data?.locked) { partialRef.current = p; PORTAL_MODE.partial = p; setPartial(p); }
      lockedRef.current = !!data?.locked;
      setLocked(!!data?.locked);
    } catch { /* keep the last known state */ }
  }, []);
  useEffect(() => {
    check();
    const iv = setInterval(check, 15000);
    const ended = () => cb.current.onEnded();
    window.addEventListener('focus', check);
    window.addEventListener('tagws-locked', check);
    window.addEventListener('tagws-session-ended', ended);
    return () => {
      clearInterval(iv);
      window.removeEventListener('focus', check);
      window.removeEventListener('tagws-locked', check);
      window.removeEventListener('tagws-session-ended', ended);
    };
  }, [check]);
  return { locked, isSuper, partial, portalOpen };
}

// Selection, filters and page kept on the server per user, restored after login / unlock.
export function useSessionState(active, snapshot, apply) {
  const hydrated = useRef(false);
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const json = JSON.stringify(snapshot);
  useEffect(() => {
    hydrated.current = false;
    if (!active) return undefined;
    let gone = false;
    notesApi.get('/notes/tag-workspace/session-state')
      .then(({ data }) => { if (!gone && data?.state) applyRef.current(data.state); })
      .catch(() => {})
      .finally(() => { if (!gone) hydrated.current = true; });
    return () => { gone = true; };
  }, [active]);
  useEffect(() => {
    if (!active || !hydrated.current) return undefined;
    const t = setTimeout(() => { notesApi.put('/notes/tag-workspace/session-state', { state: JSON.parse(json) }).catch(() => {}); }, 1000);
    return () => clearTimeout(t);
  }, [json, active]);
}
